import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';

const ACCENT = '#f97316';

type SyncDirection = 'inbound' | 'outbound' | 'bidirectional';
type SyncFrequency = 'every_5min' | 'every_15min' | 'every_30min' | 'hourly' | 'every_6h' | 'daily' | 'weekly';
type ConflictResolution = 'source_wins' | 'target_wins' | 'newest_wins' | 'manual';

interface FieldMapping {
  source_field: string;
  target_field: string;
  required: boolean;
}

interface SyncEntity {
  source_entity: string;
  target_product: string;
  target_entity: string;
  field_mappings: FieldMapping[];
  conflict_resolution: ConflictResolution;
}

interface SyncProfile {
  id: string;
  name: string;
  connection_id: string;
  connector_id: string;
  direction: SyncDirection;
  frequency: SyncFrequency;
  entities: SyncEntity[];
  status: 'active' | 'paused' | 'error' | 'disabled';
  last_sync_at: string | null;
  next_sync_at: string | null;
  sync_count: number;
  error_count: number;
  last_error?: string;
  created_at: string;
}

interface SyncRun {
  id: string;
  sync_profile_id: string;
  status: 'running' | 'completed' | 'partial' | 'failed';
  direction: SyncDirection;
  entities: {
    entity_type: string;
    records_fetched: number;
    records_created: number;
    records_updated: number;
    records_skipped: number;
    records_failed: number;
    errors: { record_id: string; message: string }[];
  }[];
  started_at: string;
  completed_at: string | null;
  duration_ms: number | null;
  trigger: 'scheduled' | 'manual';
  error?: string;
}

interface Connection {
  id: string;
  name: string;
  connector_id: string;
  status: string;
}

const DIRECTION_LABELS: Record<SyncDirection, string> = {
  inbound: 'Inbound',
  outbound: 'Outbound',
  bidirectional: 'Bidirectional',
};

const DIRECTION_COLORS: Record<SyncDirection, string> = {
  inbound: '#3b82f6',
  outbound: '#8b5cf6',
  bidirectional: '#10b981',
};

const FREQUENCY_LABELS: Record<SyncFrequency, string> = {
  every_5min: 'Every 5 min',
  every_15min: 'Every 15 min',
  every_30min: 'Every 30 min',
  hourly: 'Hourly',
  every_6h: 'Every 6 hours',
  daily: 'Daily',
  weekly: 'Weekly',
};

const STATUS_COLORS: Record<string, string> = {
  active: '#10b981',
  paused: '#eab308',
  error: '#ef4444',
  disabled: '#6b7280',
  running: '#3b82f6',
  completed: '#10b981',
  partial: '#eab308',
  failed: '#ef4444',
};

const CONNECTOR_ENTITIES: Record<string, { source: string; products: { product: string; entity: string }[] }[]> = {
  connectwise: [
    { source: 'company', products: [{ product: 'crm', entity: 'company' }, { product: 'psa', entity: 'company' }] },
    { source: 'contact', products: [{ product: 'crm', entity: 'contact' }, { product: 'psa', entity: 'contact' }] },
    { source: 'ticket', products: [{ product: 'psa', entity: 'ticket' }] },
    { source: 'time_entry', products: [{ product: 'psa', entity: 'time_entry' }] },
    { source: 'configuration', products: [{ product: 'cmdb', entity: 'configuration_item' }] },
  ],
  autotask: [
    { source: 'company', products: [{ product: 'crm', entity: 'company' }, { product: 'psa', entity: 'company' }] },
    { source: 'contact', products: [{ product: 'crm', entity: 'contact' }, { product: 'psa', entity: 'contact' }] },
    { source: 'ticket', products: [{ product: 'psa', entity: 'ticket' }] },
    { source: 'time_entry', products: [{ product: 'psa', entity: 'time_entry' }] },
    { source: 'configuration_item', products: [{ product: 'cmdb', entity: 'configuration_item' }] },
  ],
  it_glue: [
    { source: 'organization', products: [{ product: 'crm', entity: 'company' }] },
    { source: 'contact', products: [{ product: 'crm', entity: 'contact' }] },
    { source: 'configuration', products: [{ product: 'cmdb', entity: 'configuration_item' }] },
    { source: 'flexible_asset', products: [{ product: 'cmdb', entity: 'flexible_asset' }] },
    { source: 'password', products: [{ product: 'cmdb', entity: 'password' }] },
  ],
};

function formatDate(d: string | null): string {
  if (!d) return '--';
  return new Date(d).toLocaleString();
}

function formatDuration(ms: number | null): string {
  if (ms === null) return '--';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

export default function SyncProfilesPage() {
  const [profiles, setProfiles] = useState<SyncProfile[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [runsModal, setRunsModal] = useState<{ profileId: string; profileName: string } | null>(null);
  const [runs, setRuns] = useState<SyncRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);

  // Create form state
  const [formName, setFormName] = useState('');
  const [formConnectionId, setFormConnectionId] = useState('');
  const [formDirection, setFormDirection] = useState<SyncDirection>('inbound');
  const [formFrequency, setFormFrequency] = useState<SyncFrequency>('hourly');
  const [formEntities, setFormEntities] = useState<{ source: string; product: string; entity: string; selected: boolean }[]>([]);
  const [formConflict, setFormConflict] = useState<ConflictResolution>('source_wins');
  const [creating, setCreating] = useState(false);

  const loadProfiles = useCallback(async () => {
    try {
      const res = await api.get<{ sync_profiles: SyncProfile[] }>('/sync-profiles');
      setProfiles(res.sync_profiles);
    } catch {
      // ignore
    }
  }, []);

  const loadConnections = useCallback(async () => {
    try {
      const res = await api.get<{ connections: Connection[] }>('/connections');
      // Filter to external connectors only
      setConnections(res.connections.filter(c => ['connectwise', 'autotask', 'it_glue'].includes(c.connector_id)));
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    Promise.all([loadProfiles(), loadConnections()]).finally(() => setLoading(false));
  }, [loadProfiles, loadConnections]);

  // When connection changes, populate entity options
  useEffect(() => {
    if (!formConnectionId) {
      setFormEntities([]);
      return;
    }
    const conn = connections.find(c => c.id === formConnectionId);
    if (!conn) return;
    const entityDefs = CONNECTOR_ENTITIES[conn.connector_id] || [];
    const entities = entityDefs.flatMap(e =>
      e.products.map(p => ({
        source: e.source,
        product: p.product,
        entity: p.entity,
        selected: false,
      }))
    );
    setFormEntities(entities);
  }, [formConnectionId, connections]);

  async function handleSyncNow(profile: SyncProfile) {
    setSyncing(profile.id);
    try {
      await api.post(`/sync-profiles/${profile.id}/sync-now`);
      await loadProfiles();
    } catch {
      // ignore
    } finally {
      setSyncing(null);
    }
  }

  async function handleToggleStatus(profile: SyncProfile) {
    const newStatus = profile.status === 'active' ? 'paused' : 'active';
    try {
      await api.patch(`/sync-profiles/${profile.id}`, { status: newStatus });
      await loadProfiles();
    } catch {
      // ignore
    }
  }

  async function handleDelete(profile: SyncProfile) {
    if (!confirm(`Delete sync profile "${profile.name}"?`)) return;
    try {
      await api.delete(`/sync-profiles/${profile.id}`);
      await loadProfiles();
    } catch {
      // ignore
    }
  }

  async function handleCreate() {
    const selectedEntities = formEntities.filter(e => e.selected);
    if (!formName || !formConnectionId || selectedEntities.length === 0) return;

    setCreating(true);
    try {
      await api.post('/sync-profiles', {
        name: formName,
        connection_id: formConnectionId,
        direction: formDirection,
        frequency: formFrequency,
        entities: selectedEntities.map(e => ({
          source_entity: e.source,
          target_product: e.product,
          target_entity: e.entity,
          field_mappings: [],
          conflict_resolution: formConflict,
        })),
      });
      setShowCreate(false);
      setFormName('');
      setFormConnectionId('');
      setFormDirection('inbound');
      setFormFrequency('hourly');
      setFormEntities([]);
      setFormConflict('source_wins');
      await loadProfiles();
    } catch {
      // ignore
    } finally {
      setCreating(false);
    }
  }

  async function handleViewRuns(profile: SyncProfile) {
    setRunsModal({ profileId: profile.id, profileName: profile.name });
    setRunsLoading(true);
    try {
      const res = await api.get<{ sync_runs: SyncRun[] }>(`/sync-profiles/${profile.id}/runs`);
      setRuns(res.sync_runs);
    } catch {
      setRuns([]);
    } finally {
      setRunsLoading(false);
    }
  }

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Loading sync profiles...</div>;
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', margin: 0 }}>Sync Profiles</h1>
          <p style={{ color: '#6b7280', margin: '0.25rem 0 0', fontSize: '0.875rem' }}>
            Recurring bidirectional sync between external systems and The One Stack
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          style={{
            backgroundColor: ACCENT,
            color: '#fff',
            border: 'none',
            borderRadius: '0.5rem',
            padding: '0.625rem 1.25rem',
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: '0.875rem',
          }}
        >
          + New Sync Profile
        </button>
      </div>

      {/* Profile List */}
      {profiles.length === 0 ? (
        <div style={{
          backgroundColor: '#fff',
          borderRadius: '0.75rem',
          border: '1px solid #e5e7eb',
          padding: '3rem',
          textAlign: 'center',
        }}>
          <p style={{ fontSize: '1.125rem', fontWeight: 600, color: '#374151' }}>No sync profiles yet</p>
          <p style={{ color: '#6b7280', marginTop: '0.5rem' }}>Create a sync profile to start recurring data synchronization.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {profiles.map(profile => (
            <div
              key={profile.id}
              style={{
                backgroundColor: '#fff',
                borderRadius: '0.75rem',
                border: '1px solid #e5e7eb',
                padding: '1.25rem',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                    <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#111827', margin: 0 }}>{profile.name}</h3>
                    <span style={{
                      display: 'inline-block',
                      padding: '0.125rem 0.5rem',
                      borderRadius: '9999px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      backgroundColor: `${STATUS_COLORS[profile.status]}20`,
                      color: STATUS_COLORS[profile.status],
                    }}>
                      {profile.status}
                    </span>
                    <span style={{
                      display: 'inline-block',
                      padding: '0.125rem 0.5rem',
                      borderRadius: '9999px',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      backgroundColor: `${DIRECTION_COLORS[profile.direction]}15`,
                      color: DIRECTION_COLORS[profile.direction],
                    }}>
                      {DIRECTION_LABELS[profile.direction]}
                    </span>
                  </div>

                  <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.8125rem', color: '#6b7280', flexWrap: 'wrap' }}>
                    <span>Connector: <strong style={{ color: '#374151' }}>{profile.connector_id}</strong></span>
                    <span>Frequency: <strong style={{ color: '#374151' }}>{FREQUENCY_LABELS[profile.frequency]}</strong></span>
                    <span>Entities: <strong style={{ color: '#374151' }}>{profile.entities.map(e => e.source_entity).join(', ')}</strong></span>
                    <span>Syncs: <strong style={{ color: '#374151' }}>{profile.sync_count}</strong></span>
                  </div>

                  <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.8125rem', color: '#6b7280', marginTop: '0.375rem' }}>
                    <span>Last sync: {formatDate(profile.last_sync_at)}</span>
                    <span>Next sync: {formatDate(profile.next_sync_at)}</span>
                    {profile.error_count > 0 && (
                      <span style={{ color: '#ef4444' }}>Errors: {profile.error_count}</span>
                    )}
                  </div>

                  {profile.last_error && (
                    <div style={{
                      marginTop: '0.5rem',
                      padding: '0.5rem 0.75rem',
                      backgroundColor: '#fef2f2',
                      borderRadius: '0.375rem',
                      fontSize: '0.75rem',
                      color: '#991b1b',
                    }}>
                      {profile.last_error}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, marginLeft: '1rem' }}>
                  <button
                    onClick={() => handleSyncNow(profile)}
                    disabled={syncing === profile.id || profile.status === 'disabled'}
                    style={{
                      padding: '0.375rem 0.75rem',
                      borderRadius: '0.375rem',
                      border: '1px solid #d1d5db',
                      backgroundColor: syncing === profile.id ? '#f3f4f6' : '#fff',
                      cursor: syncing === profile.id ? 'not-allowed' : 'pointer',
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      color: '#374151',
                    }}
                  >
                    {syncing === profile.id ? 'Syncing...' : 'Sync Now'}
                  </button>
                  <button
                    onClick={() => handleViewRuns(profile)}
                    style={{
                      padding: '0.375rem 0.75rem',
                      borderRadius: '0.375rem',
                      border: '1px solid #d1d5db',
                      backgroundColor: '#fff',
                      cursor: 'pointer',
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      color: '#374151',
                    }}
                  >
                    History
                  </button>
                  <button
                    onClick={() => handleToggleStatus(profile)}
                    style={{
                      padding: '0.375rem 0.75rem',
                      borderRadius: '0.375rem',
                      border: '1px solid #d1d5db',
                      backgroundColor: '#fff',
                      cursor: 'pointer',
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      color: profile.status === 'active' ? '#eab308' : '#10b981',
                    }}
                  >
                    {profile.status === 'active' ? 'Pause' : 'Resume'}
                  </button>
                  <button
                    onClick={() => handleDelete(profile)}
                    style={{
                      padding: '0.375rem 0.75rem',
                      borderRadius: '0.375rem',
                      border: '1px solid #fca5a5',
                      backgroundColor: '#fff',
                      cursor: 'pointer',
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      color: '#ef4444',
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: '0.75rem', padding: '1.5rem',
            width: 560, maxHeight: '90vh', overflow: 'auto',
          }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', margin: '0 0 1.25rem' }}>
              Create Sync Profile
            </h2>

            {/* Name */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.375rem' }}>
                Profile Name
              </label>
              <input
                value={formName}
                onChange={e => setFormName(e.target.value)}
                placeholder="e.g., ConnectWise Companies Sync"
                style={{
                  width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.375rem',
                  border: '1px solid #d1d5db', fontSize: '0.875rem', boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Connection */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.375rem' }}>
                Connection
              </label>
              {connections.length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>No external connections configured. Create a connection first.</p>
              ) : (
                <select
                  value={formConnectionId}
                  onChange={e => setFormConnectionId(e.target.value)}
                  style={{
                    width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.375rem',
                    border: '1px solid #d1d5db', fontSize: '0.875rem', boxSizing: 'border-box',
                  }}
                >
                  <option value="">Select a connection...</option>
                  {connections.map(c => (
                    <option key={c.id} value={c.id}>{c.name} ({c.connector_id})</option>
                  ))}
                </select>
              )}
            </div>

            {/* Direction */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.375rem' }}>
                Direction
              </label>
              <select
                value={formDirection}
                onChange={e => setFormDirection(e.target.value as SyncDirection)}
                style={{
                  width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.375rem',
                  border: '1px solid #d1d5db', fontSize: '0.875rem', boxSizing: 'border-box',
                }}
              >
                <option value="inbound">Inbound (External to The One Stack)</option>
                <option value="outbound">Outbound (The One Stack to External)</option>
                <option value="bidirectional">Bidirectional</option>
              </select>
            </div>

            {/* Frequency */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.375rem' }}>
                Frequency
              </label>
              <select
                value={formFrequency}
                onChange={e => setFormFrequency(e.target.value as SyncFrequency)}
                style={{
                  width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.375rem',
                  border: '1px solid #d1d5db', fontSize: '0.875rem', boxSizing: 'border-box',
                }}
              >
                {Object.entries(FREQUENCY_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>

            {/* Entities */}
            {formEntities.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.375rem' }}>
                  Entities to Sync
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                  {formEntities.map((ent, i) => (
                    <label
                      key={`${ent.source}-${ent.product}-${ent.entity}`}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                        padding: '0.5rem 0.75rem', backgroundColor: '#f9fafb',
                        borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={ent.selected}
                        onChange={() => {
                          const updated = [...formEntities];
                          updated[i] = { ...ent, selected: !ent.selected };
                          setFormEntities(updated);
                        }}
                      />
                      <span style={{ color: '#374151' }}>{ent.source}</span>
                      <span style={{ color: '#9ca3af' }}>-&gt;</span>
                      <span style={{ color: '#6b7280' }}>{ent.product}/{ent.entity}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Conflict Resolution */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.375rem' }}>
                Conflict Resolution
              </label>
              <select
                value={formConflict}
                onChange={e => setFormConflict(e.target.value as ConflictResolution)}
                style={{
                  width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.375rem',
                  border: '1px solid #d1d5db', fontSize: '0.875rem', boxSizing: 'border-box',
                }}
              >
                <option value="source_wins">Source Wins</option>
                <option value="target_wins">Target Wins</option>
                <option value="newest_wins">Newest Wins</option>
                <option value="manual">Manual</option>
              </select>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button
                onClick={() => setShowCreate(false)}
                style={{
                  padding: '0.5rem 1rem', borderRadius: '0.375rem', border: '1px solid #d1d5db',
                  backgroundColor: '#fff', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={creating || !formName || !formConnectionId || formEntities.filter(e => e.selected).length === 0}
                style={{
                  padding: '0.5rem 1rem', borderRadius: '0.375rem', border: 'none',
                  backgroundColor: ACCENT, color: '#fff', cursor: creating ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem', fontWeight: 600, opacity: creating ? 0.7 : 1,
                }}
              >
                {creating ? 'Creating...' : 'Create Sync Profile'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Runs Modal */}
      {runsModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: '0.75rem', padding: '1.5rem',
            width: 700, maxHeight: '80vh', overflow: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#111827', margin: 0 }}>
                Sync History: {runsModal.profileName}
              </h2>
              <button
                onClick={() => { setRunsModal(null); setRuns([]); }}
                style={{
                  border: 'none', background: 'none', cursor: 'pointer',
                  fontSize: '1.25rem', color: '#6b7280', padding: '0.25rem',
                }}
              >
                x
              </button>
            </div>

            {runsLoading ? (
              <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>Loading runs...</p>
            ) : runs.length === 0 ? (
              <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>No sync runs yet.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
                    <th style={{ padding: '0.5rem', color: '#6b7280', fontWeight: 500 }}>Status</th>
                    <th style={{ padding: '0.5rem', color: '#6b7280', fontWeight: 500 }}>Trigger</th>
                    <th style={{ padding: '0.5rem', color: '#6b7280', fontWeight: 500 }}>Started</th>
                    <th style={{ padding: '0.5rem', color: '#6b7280', fontWeight: 500 }}>Duration</th>
                    <th style={{ padding: '0.5rem', color: '#6b7280', fontWeight: 500 }}>Created</th>
                    <th style={{ padding: '0.5rem', color: '#6b7280', fontWeight: 500 }}>Failed</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map(run => {
                    const totalCreated = run.entities.reduce((s, e) => s + e.records_created, 0);
                    const totalFailed = run.entities.reduce((s, e) => s + e.records_failed, 0);
                    return (
                      <tr key={run.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '0.5rem' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '0.125rem 0.5rem',
                            borderRadius: '9999px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            backgroundColor: `${STATUS_COLORS[run.status]}20`,
                            color: STATUS_COLORS[run.status],
                          }}>
                            {run.status}
                          </span>
                        </td>
                        <td style={{ padding: '0.5rem', color: '#374151' }}>{run.trigger}</td>
                        <td style={{ padding: '0.5rem', color: '#374151' }}>{formatDate(run.started_at)}</td>
                        <td style={{ padding: '0.5rem', color: '#374151' }}>{formatDuration(run.duration_ms)}</td>
                        <td style={{ padding: '0.5rem', color: '#10b981', fontWeight: 600 }}>{totalCreated}</td>
                        <td style={{ padding: '0.5rem', color: totalFailed > 0 ? '#ef4444' : '#374151', fontWeight: totalFailed > 0 ? 600 : 400 }}>
                          {totalFailed}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
